import { AURA_STATS, PROTOCOLS, type Protocol } from "@zend/content";
import type { StatBlock } from "../units/stats.js";
import type { Rng } from "../rng.js";
import type { battleMorale } from "./tactics.js";
import type { BattleContext, BattleEvent, CombatSlot, CombatantInput, CombatantMeta, ResolvedAbility, Side, SlotKind } from "./types.js";

export type StatusKey = "atk_pct" | "def_pct" | "speed_pct" | "acc_pct" | "marked" | "taunt" | "stun" | "corrode" | "fortify";

export interface Status {
  key: StatusKey;
  /** Percent or magnitude of the effect. */
  value: number;
  /** Tick at which the effect ends (exclusive). */
  until: number;
  /** Who applied it (corrode uses the source attack for its damage). */
  source: string;
}

export interface Unit {
  input: CombatantInput;
  meta: CombatantMeta;
  side: Side;
  slot: CombatSlot;
  kind: SlotKind;
  stats: StatBlock;
  maxHp: number;
  hp: number;
  energy: number;
  shield: number;
  alive: boolean;
  statuses: Status[];
  nextAttackTick: number;
  abilities: ResolvedAbility[];
  protocol: Protocol | null;
  protocolBonusPct: number;
  /** Summary counters. */
  damageDealt: number;
  damageTaken: number;
  healing: number;
  kills: number;
  abilitiesUsed: number;
}

export interface SideState {
  side: Side;
  units: Unit[];
  modifiers: { atkPct: number; defPct: number; hpPct: number; auraPct: number; revengePct: number };
  summoned: number;
  wallHp: number;
  wallAbsorbed: number;
  morale?: ReturnType<typeof battleMorale>;
}

export interface BattleState {
  rulesVersion?: 1 | 2;
  tick: number;
  rng: Rng;
  context: BattleContext;
  sides: Record<Side, SideState>;
  events: BattleEvent[];
  trapDamage: number;
  turretDamage: number;
  nextExtraId: number;
}

export function other(side: Side): Side {
  return side === "attacker" ? "defender" : "attacker";
}

export function status(u: Unit, key: StatusKey): Status | undefined {
  return u.statuses.find((s) => s.key === key);
}

export function statusValue(u: Unit, key: StatusKey): number {
  return status(u, key)?.value ?? 0;
}

/** Refresh-not-stack: the same effect key replaces its predecessor, different effects coexist (R30). */
export function applyStatus(state: BattleState, u: Unit, key: StatusKey, value: number, ticks: number, source: string): void {
  const until = state.tick + Math.max(1, Math.round(ticks));
  const existing = status(u, key);
  if (existing) {
    existing.value = value;
    existing.until = until;
    existing.source = source;
  } else u.statuses.push({ key, value, until, source });
  state.events.push({ t: state.tick, k: "status", a: source, b: u.input.id, v: Math.round(value), x: key });
}

export function expireStatuses(state: BattleState, u: Unit): void {
  u.statuses = u.statuses.filter((s) => s.until > state.tick);
}

/** Sum of a living support's aura for one stat on the given side, scaled by aura modifiers. */
export function auraPct(side: SideState, stat: (typeof AURA_STATS)[number]): number {
  let total = 0;
  for (const u of side.units) {
    if (!u.alive || u.kind !== "support") continue;
    for (const ab of u.abilities) {
      if (ab.archetype !== "abl.aura") continue;
      const key = AURA_STATS[Math.round(ab.params["stat"] ?? 0)] ?? "atk";
      if (key === stat) total += (ab.params["pct"] ?? 0) * ab.magnitude;
    }
  }
  return total * (1 + side.modifiers.auraPct / 100);
}

export function effectiveAtk(state: BattleState, u: Unit): number {
  const side = state.sides[u.side];
  const pct = statusValue(u, "atk_pct") + auraPct(side, "atk") + side.modifiers.atkPct;
  return u.stats.atk * (1 + pct / 100) * (1 + (u.input.kind === "unit" ? (side.morale?.atkPct ?? 0) / 100 : 0));
}

export function effectiveDef(state: BattleState, u: Unit): number {
  const side = state.sides[u.side];
  const pct = statusValue(u, "def_pct") + statusValue(u, "fortify") + auraPct(side, "def") + side.modifiers.defPct;
  return Math.max(0, u.stats.def * (1 + pct / 100));
}

export function effectiveCrit(state: BattleState, u: Unit): number {
  return Math.min(1, u.stats.crit + auraPct(state.sides[u.side], "crit") / 100);
}

export function effectiveInterval(u: Unit): number {
  const pct = statusValue(u, "speed_pct");
  return Math.max(3, Math.round(u.stats.intervalTicks / (1 + pct / 100)));
}

export function energyGain(state: BattleState, u: Unit, base: number): number {
  return Math.round(base * (1 + auraPct(state.sides[u.side], "energy") / 100));
}

export function protocolFromRule(rule: number | undefined): Protocol {
  return PROTOCOLS[Math.round(rule ?? 0)] ?? "front";
}

export function livingUnits(side: SideState, kind?: SlotKind): Unit[] {
  return side.units.filter((u) => u.alive && (kind === undefined || u.kind === kind));
}

export function hpShare(side: SideState): number {
  let max = 0;
  let cur = 0;
  for (const u of side.units) {
    if (u.kind === "turret") continue;
    max += u.maxHp;
    cur += u.alive ? u.hp : 0;
  }
  return max > 0 ? cur / max : 0;
}
